import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Grid, Paper, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress, Chip, Menu, MenuItem, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LagbasLogo from '../../../../components/LagbasLogo';
import { db } from '../../../../firebase/config';
import { useAuth } from '../../../../contexts/AuthContext';
import {
  collection, getDocs, addDoc, deleteDoc, doc, query,
  orderBy, serverTimestamp, writeBatch,
} from 'firebase/firestore';
import { Toaster, toast } from 'sonner';
import {
  MAPPN_ACCENT, DEFAULT_PERSPECTIVES, DEFAULT_CATEGORIES,
  defaultTechStack, uid,
} from '../constants';

function fmtDate(ts) {
  if (!ts) return '–';
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('sv-SE');
}

export default function MappNLanding() {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });
  const [creating, setCreating] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuCase, setMenuCase] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { load(); }, []); // eslint-disable-line

  async function load() {
    setLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, 'mappnCases'), orderBy('createdAt', 'desc'))
      );
      setCases(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  async function handleCreate() {
    if (!form.name.trim()) return;
    setCreating(true);
    try {
      const companyId = userProfile?.companyId || currentUser?.uid;
      const caseRef = await addDoc(collection(db, 'mappnCases'), {
        name: form.name.trim(),
        description: form.description.trim(),
        ownerCompanyId: companyId,
        techStack: defaultTechStack(),
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Create default subcollections in batch
      const batch = writeBatch(db);
      DEFAULT_PERSPECTIVES.forEach(p => {
        const ref = doc(collection(db, 'mappnCases', caseRef.id, 'perspectives'));
        batch.set(ref, { ...p, createdAt: serverTimestamp() });
      });
      DEFAULT_CATEGORIES.forEach(c => {
        const ref = doc(collection(db, 'mappnCases', caseRef.id, 'categories'));
        batch.set(ref, { ...c, createdAt: serverTimestamp() });
      });
      await batch.commit();

      setCreateOpen(false);
      setForm({ name: '', description: '' });
      navigate(`/applab/mappn/${caseRef.id}/cards`);
    } catch (e) {
      console.error(e);
      toast.error('Kunde inte skapa caset');
    }
    setCreating(false);
  }

  async function handleDelete(c) {
    if (!window.confirm(`Ta bort "${c.name}"? Alla komponenter raderas.`)) return;
    setDeleting(true);
    try {
      // Delete subcollection documents
      for (const sub of ['perspectives', 'categories', 'components']) {
        const subSnap = await getDocs(collection(db, 'mappnCases', c.id, sub));
        const batch = writeBatch(db);
        subSnap.docs.forEach(d => batch.delete(d.ref));
        if (subSnap.docs.length > 0) await batch.commit();
      }
      await deleteDoc(doc(db, 'mappnCases', c.id));
      setCases(prev => prev.filter(x => x.id !== c.id));
      toast.success(`"${c.name}" raderat`);
    } catch (e) {
      console.error(e);
      toast.error('Kunde inte radera caset');
    }
    setDeleting(false);
    setMenuAnchor(null);
  }

  async function handleDuplicate(c) {
    setMenuAnchor(null);
    try {
      const companyId = userProfile?.companyId || currentUser?.uid;
      const newRef = await addDoc(collection(db, 'mappnCases'), {
        name: `${c.name} (kopia)`,
        description: c.description || '',
        ownerCompanyId: companyId,
        techStack: (c.techStack || []).map(t => ({ ...t, id: uid() })),
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const batch = writeBatch(db);
      for (const sub of ['perspectives', 'categories', 'components']) {
        const subSnap = await getDocs(collection(db, 'mappnCases', c.id, sub));
        subSnap.docs.forEach(d => {
          const ref = doc(collection(db, 'mappnCases', newRef.id, sub));
          batch.set(ref, { ...d.data(), id: undefined });
        });
      }
      await batch.commit();
      toast.success('Case duplicerat');
      await load();
    } catch (e) {
      console.error(e);
      toast.error('Kunde inte duplicera caset');
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0d1117', color: 'white' }}>
      <Toaster richColors position="top-center" />

      {/* Header */}
      <Box sx={{
        bgcolor: '#161b22', borderBottom: '1px solid rgba(255,255,255,0.06)',
        py: 1.5, px: 3, display: 'flex', alignItems: 'center', gap: 2,
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <IconButton color="inherit" size="small" onClick={() => navigate('/applab')}>
          <ArrowBackIcon />
        </IconButton>
        <AccountTreeIcon sx={{ color: MAPPN_ACCENT, fontSize: '1.4rem' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>MappN</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
            Systemarkitektur och plattformskartläggning
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setForm({ name: '', description: '' }); setCreateOpen(true); }}
          sx={{ bgcolor: MAPPN_ACCENT, '&:hover': { bgcolor: '#1976D2' }, textTransform: 'none', fontWeight: 700 }}
        >
          Nytt case
        </Button>
      </Box>

      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3, py: 5 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: MAPPN_ACCENT }} />
          </Box>
        ) : cases.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 12 }}>
            <AccountTreeIcon sx={{ fontSize: 64, color: 'rgba(255,255,255,0.1)', mb: 2 }} />
            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.5)', mb: 1 }}>
              Inga case ännu
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)', mb: 3 }}>
              Skapa ditt första case för att börja kartlägga en plattform eller ett system.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
              sx={{ bgcolor: MAPPN_ACCENT, textTransform: 'none' }}
            >
              Skapa första caset
            </Button>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {cases.map(c => (
              <Grid item xs={12} sm={6} md={4} key={c.id}>
                <Paper
                  onClick={() => navigate(`/applab/mappn/${c.id}/cards`)}
                  sx={{
                    bgcolor: '#161b22',
                    border: '1px solid rgba(255,255,255,0.07)',
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                    '&:hover': {
                      borderColor: MAPPN_ACCENT + '66',
                      boxShadow: `0 0 0 1px ${MAPPN_ACCENT}33`,
                    },
                    overflow: 'hidden',
                    display: 'flex',
                    minHeight: 100,
                  }}
                >
                  {/* Left: video or image thumbnail */}
                  {(c.heroVideoUrl || c.heroImageUrl) && (
                    <Box sx={{ width: 90, flexShrink: 0, position: 'relative', overflow: 'hidden' }}>
                      {c.heroVideoUrl ? (
                        <Box
                          component="video"
                          src={c.heroVideoUrl}
                          autoPlay muted loop playsInline
                          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      ) : (
                        <Box
                          component="img"
                          src={c.heroImageUrl}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      )}
                    </Box>
                  )}

                  {/* Right: content */}
                  <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                      <AccountTreeIcon sx={{ color: MAPPN_ACCENT, fontSize: '1.1rem', mr: 0.75, mt: 0.2, flexShrink: 0 }} />
                      <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1, color: 'white', lineHeight: 1.3, fontSize: '0.92rem' }}>
                        {c.name}
                      </Typography>
                      <IconButton
                        size="small"
                        sx={{ color: 'rgba(255,255,255,0.3)', ml: 0.5, mt: -0.5 }}
                        onClick={e => { e.stopPropagation(); setMenuCase(c); setMenuAnchor(e.currentTarget); }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {c.description && (
                      <Typography variant="body2" sx={{
                        color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem',
                        lineHeight: 1.5, mb: 1,
                        display: '-webkit-box', WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      }}>
                        {c.description}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 'auto' }}>
                      {c.componentCount > 0 && (
                        <Chip label={`${c.componentCount} komp.`} size="small"
                          sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)', fontSize: '0.68rem', height: 20 }} />
                      )}
                      {(c.techStack || []).length > 0 && (
                        <Chip label={`${c.techStack.length} tech`} size="small"
                          sx={{ bgcolor: MAPPN_ACCENT + '22', color: MAPPN_ACCENT, fontSize: '0.68rem', height: 20 }} />
                      )}
                    </Box>

                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)', display: 'block', mt: 1 }}>
                      Uppdaterat {fmtDate(c.updatedAt)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Context menu */}
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        PaperProps={{ sx: { bgcolor: '#1e2530', color: 'white', border: '1px solid rgba(255,255,255,0.08)' } }}
      >
        <MenuItem
          onClick={() => { handleDuplicate(menuCase); }}
          sx={{ fontSize: '0.85rem', gap: 1.5 }}
          disabled={deleting}
        >
          <ContentCopyIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.5)' }} />
          Duplicera
        </MenuItem>
        <MenuItem
          onClick={() => handleDelete(menuCase)}
          sx={{ fontSize: '0.85rem', gap: 1.5, color: '#EF5350' }}
          disabled={deleting}
        >
          <DeleteOutlineIcon fontSize="small" />
          Ta bort
        </MenuItem>
      </Menu>

      {/* Create dialog */}
      <Dialog
        open={createOpen}
        onClose={() => !creating && setCreateOpen(false)}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: '#161b22', color: 'white', border: '1px solid rgba(255,255,255,0.1)' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Nytt case</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField
            label="Namn"
            value={form.name}
            onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            fullWidth size="small" autoFocus
            onKeyDown={e => e.key === 'Enter' && handleCreate()}
            sx={darkField}
          />
          <TextField
            label="Beskrivning (valfri)"
            value={form.description}
            onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            fullWidth size="small" multiline rows={2}
            sx={darkField}
          />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>
            Tre default-perspektiv (Kund, Internt, Bolag) och fem kategorier skapas automatiskt.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={creating} sx={{ color: 'rgba(255,255,255,0.5)' }}>
            Avbryt
          </Button>
          <Button
            variant="contained"
            disabled={!form.name.trim() || creating}
            onClick={handleCreate}
            sx={{ bgcolor: MAPPN_ACCENT, '&:hover': { bgcolor: '#1976D2' }, textTransform: 'none' }}
          >
            {creating ? 'Skapar…' : 'Skapa och öppna'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const darkField = {
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)' },
  '& .MuiOutlinedInput-root': {
    color: 'white',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
    '&.Mui-focused fieldset': { borderColor: MAPPN_ACCENT },
  },
};
